import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { X, CreditCard } from 'lucide-react';
import { Cuenta } from '../types';
import { IconPicker, GRUPOS_ICONOS } from './IconPicker';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

const GRUPOS_CUENTAS = GRUPOS_ICONOS.filter(g =>
  ['Finanzas', 'Hogar', 'Tecnología', 'Viajes', 'Otros'].includes(g.label)
);

interface Props {
  cuenta: Cuenta;
  onClose: () => void;
}

export const ModalEditarCuenta: React.FC<Props> = ({ cuenta, onClose }) => {
  useCerrarConEscape(onClose);
  const { actualizarCuenta } = useData();
  const [nombre, setNombre]             = useState(cuenta.nombre);
  const [tipo, setTipo]                 = useState(cuenta.tipo);
  const [icono, setIcono]               = useState(cuenta.icono || '🏦');
  const [saldoInicial, setSaldoInicial] = useState((cuenta.saldoInicial ?? cuenta.saldo).toString());
  const [objetivo, setObjetivo]         = useState(cuenta.objetivo?.toString() || '');
  const [ocultarSaldo, setOcultarSaldo] = useState(cuenta.ocultarSaldo);
  const [esCredito, setEsCredito]       = useState(cuenta.esCredito ?? false);
  // Tarjetas de Apple Pay que apuntan sus pagos aquí (se añaden al pagar)
  const [tarjetas, setTarjetas]         = useState(cuenta.tarjetasApplePay ?? []);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  const inputClass = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white";
  const labelClass = "block text-gray-700 dark:text-gray-300 font-semibold mb-2";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!nombre) { setError('Ponle un nombre a la cuenta'); return; }
    setLoading(true);
    try {
      await actualizarCuenta(cuenta.id, {
        nombre,
        tipo,
        icono,
        ocultarSaldo,
        esCredito,
        saldoInicial: saldoInicial === '' ? 0 : parseFloat(saldoInicial),
        ...(cuenta.tarjetasApplePay ? { tarjetasApplePay: tarjetas } : {}),
        ...(tipo === 'hucha' && objetivo ? { objetivo: aNumero(objetivo) } : {}),
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center capa-modal z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Editar Cuenta</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className={labelClass}>Nombre</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              className={inputClass} required />
          </div>

          <div>
            <label className={labelClass}>Tipo</label>
            <select value={tipo} onChange={e => setTipo(e.target.value as Cuenta['tipo'])}
              className={inputClass}>
              <option value="corriente">Corriente</option>
              <option value="ahorro">Ahorro</option>
              <option value="tarjeta">Tarjeta de Crédito</option>
              <option value="hucha">🐷 Hucha</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Icono</label>
            <IconPicker value={icono} onChange={setIcono} grupos={GRUPOS_CUENTAS} />
          </div>

          <div>
            <label className={labelClass}>Saldo Inicial (€)</label>
            <input type="number" step="0.01" value={saldoInicial}
              onChange={e => setSaldoInicial(e.target.value)}
              className={inputClass} placeholder="0,00" />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              El saldo actual se calcula automáticamente: Saldo Inicial + Ingresos − Gastos
            </p>
          </div>

          {tipo === 'hucha' && (
            <div>
              <label className={labelClass}>Importe objetivo (€) — opcional</label>
              <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE} title={AVISO_IMPORTE} value={objetivo}
                onChange={e => setObjetivo(e.target.value)}
                className={inputClass} placeholder="Ej: 90.00" />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Importe total que quieres acumular</p>
            </div>
          )}

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={ocultarSaldo}
                onChange={e => setOcultarSaldo(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded" />
              <span className="text-gray-700 dark:text-gray-300 font-semibold">Ocultar saldo</span>
            </label>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={esCredito}
                onChange={e => setEsCredito(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded" />
              <span className="text-gray-700 dark:text-gray-300 font-semibold">Tarjeta de crédito</span>
            </label>
          </div>

          {(cuenta.tarjetasApplePay?.length ?? 0) > 0 && (
            <div>
              <label className={labelClass}>Tarjetas de Apple Pay</label>
              {tarjetas.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Ninguna. Al guardar, sus pagos te preguntarán la cuenta.</p>
              ) : (
                <ul className="space-y-1.5">
                  {tarjetas.map(t => (
                    <li key={t} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <CreditCard className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="flex-1 min-w-0 truncate capitalize">{t}</span>
                      <button type="button" onClick={() => setTarjetas(ts => ts.filter(x => x !== t))}
                        className="text-xs font-semibold text-red-600 dark:text-red-400 px-2 py-1 min-h-[32px]">
                        Quitar
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Los pagos con estas tarjetas se apuntan en esta cuenta. Si quitas una, la próxima vez te preguntará.
              </p>
            </div>
          )}

          <div className="flex gap-4 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
